/**
 * Feedback Answer Formatters — Pure utility functions for formatting
 * correct answers and user answers across all question types.
 *
 * Extracted from FeedbackDisplay.tsx (691→~300 lines) during CL-1 prep s62.
 * Co-located in practice/ domain (CL-1 s64).
 */

import type { Question, ScheduleEvent, MatchingPair } from '../../../content'
import { error as logError } from '../../../utils/logger'

// ── Number / Money Helpers ───────────────────────────────────

export function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n)
}

export function normalizeCents(n: unknown): number | null {
  if (!isFiniteNumber(n)) return null
  return Math.max(0, Math.round(n))
}

export function formatMoneyDisplay(cents: number): string {
  const safe = Math.max(0, Math.round(cents))
  const euros = Math.floor(safe / 100)
  const remaining = safe % 100
  if (remaining === 0) return `€${euros},-`
  return `€${euros},${remaining.toString().padStart(2, '0')}`
}

// ── Correct Answer Formatting ────────────────────────────────

export function formatCorrectAnswer(question: Question): string {
  const isMoneyAssembler = question.questionType === 'money'
  const isVisualInteractive = [
    'bar_chart',
    'line_chart',
    'schedule_reading',
  ].includes(question.questionType || '')

  if (isMoneyAssembler) {
    const moneyConfig = question.data?.money
    if (!moneyConfig || typeof moneyConfig.targetCents !== 'number') {
      logError('FeedbackDisplay: Missing targetCents in question.data.money')
      return '€0,00'
    }
    const cents = normalizeCents(moneyConfig.targetCents)
    return cents === null ? '' : formatMoneyDisplay(cents)
  }

  if (isVisualInteractive) {
    const config = question.data || {}

    if (
      question.questionType === 'bar_chart' ||
      question.questionType === 'line_chart'
    ) {
      let chartData: Array<{ label: string; value: number }> = []
      if (Array.isArray(config.data) && config.data.length > 0) {
        chartData = config.data
      } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
        chartData = config.labels.map((label: string, i: number) => ({
          label,
          value: config.values?.[i] ?? 0,
        }))
      }
      const item = chartData[config.correctIndex]
      return item?.label || 'Correct antwoord'
    }

    if (question.questionType === 'schedule_reading') {
      const schedule = config.schedule || { events: [] }
      const correctCell = config.correctCell
      if (correctCell) {
        const event = schedule.events?.find(
          (e: ScheduleEvent) =>
            e.row === correctCell.row && e.col === correctCell.col,
        )
        if (event) {
          const colName = schedule.columns?.[correctCell.col] || ''
          const timeName = schedule.timeSlots?.[correctCell.row] || ''
          return `${event.content} (${colName}, ${timeName})`
        }
      }
      return 'Correct vakje'
    }
    return 'Correct antwoord'
  }

  if (question.questionType === 'ordering') {
    const config = question.data || {}
    const items = config.items || question.options || []
    const correctOrder = config.correctOrder
    if (correctOrder && items.length > 0) {
      return correctOrder.map((idx: number) => items[idx]).join(' → ')
    }
    return items.join(' → ')
  }

  if (question.questionType === 'fill_in') {
    const config = question.data || {}
    const accepted = config.acceptedAnswers
    if (accepted && accepted.length > 0) return accepted[0]
  }

  if (question.questionType === 'matching') {
    const config = question.data || {}
    const pairs = config.pairs
    if (pairs && pairs.length > 0) {
      return pairs.map((p: MatchingPair) => `${p.left} → ${p.right}`).join(', ')
    }
    return 'Correct antwoord'
  }

  return question.options?.[question.correctAnswer] ?? 'Correct antwoord'
}

// ── User Answer Formatting ───────────────────────────────────

export function formatUserAnswer(
  question: Question,
  userAnswer: string,
): string {
  if (question.questionType === 'ordering') {
    const config = question.data || {}
    const items = config.items || question.options || []
    if (items.length > 0 && userAnswer && userAnswer.includes(',')) {
      try {
        const indices = userAnswer.split(',').map(Number)
        if (
          indices.every((i: number) => !isNaN(i) && i >= 0 && i < items.length)
        ) {
          return indices.map((i: number) => items[i]).join(' → ')
        }
      } catch {
        /* fallthrough */
      }
    }
  }

  if (question.questionType === 'matching') {
    const config = question.data || {}
    const pairs = config.pairs
    if (pairs && pairs.length > 0 && userAnswer) {
      try {
        const userPairs: [number, number][] = JSON.parse(userAnswer)
        if (Array.isArray(userPairs) && userPairs.length > 0) {
          return userPairs
            .map(([leftIdx, rightIdx]) => {
              const left = pairs[leftIdx]?.left ?? '?'
              const right = pairs[rightIdx]?.right ?? '?'
              return `${left} → ${right}`
            })
            .join(', ')
        }
      } catch {
        /* fallthrough */
      }
    }
    return 'Geen antwoord'
  }

  return userAnswer || ''
}

// ── Money Token Parsing ──────────────────────────────────────

export interface MoneyToken {
  valueCents: number
  count: number
}

export function parseMoneyTokens(userAnswer: string): MoneyToken[] | null {
  try {
    const parsed = JSON.parse(userAnswer)
    if (!Array.isArray(parsed)) return null
    if (
      !parsed.every(
        (t) => isFiniteNumber(t?.valueCents) && isFiniteNumber(t?.count),
      )
    )
      return null
    return parsed
  } catch {
    return null
  }
}
